import { makeCancelablePromise } from '../util/PromiseUtil';
import { Executor, HttpClientResponse, QueryParameters, RequestHeaders } from './Definitions';

/**
 * This class is just internal to this file, and provides a way for services to be wrapped and
 * monitored for cancellation.
 */
export class ServiceProviderExecutor<T extends string> implements Executor<T> {
  private cancelableExecutor: Executor<T>;
  private cancels: (() => void)[];

  constructor(cancelableExecutor: Executor<T>) {
    this.cancelableExecutor = cancelableExecutor;
    this.cancels = [];
  }

  get(
    url: T,
    headers?: RequestHeaders,
    queryParams?: QueryParameters,
    additionalParams?: Partial<RequestInit>
  ): Promise<HttpClientResponse> {
    return this.getPromise(this.cancelableExecutor.get(url, headers, queryParams, additionalParams));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  post(url: T, body: any, headers?: RequestHeaders, queryParams?: QueryParameters): Promise<HttpClientResponse> {
    return this.getPromise(this.cancelableExecutor.post(url, body, headers, queryParams));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  put(url: T, body: any, headers?: RequestHeaders, queryParams?: QueryParameters): Promise<HttpClientResponse> {
    return this.getPromise(this.cancelableExecutor.put(url, body, headers, queryParams));
  }

  delete(url: T, headers?: RequestHeaders, queryParams?: QueryParameters): Promise<HttpClientResponse> {
    return this.getPromise(this.cancelableExecutor.delete(url, headers, queryParams));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  patch(url: T, body: any, headers?: RequestHeaders, queryParams?: QueryParameters): Promise<HttpClientResponse> {
    return this.getPromise(this.cancelableExecutor.patch(url, body, headers, queryParams));
  }

  private getPromise(promise: Promise<HttpClientResponse>) {
    const cancelablePromise = makeCancelablePromise(promise);
    this.cancels.push(cancelablePromise.cancel);
    return cancelablePromise.promise;
  }

  cancelAll() {
    this.cancels.forEach((cancel) => cancel());
    this.clearCancels();
  }

  private clearCancels() {
    this.cancels = [];
  }
}
